"use client";

import { useState } from "react";
import { X } from "lucide-react";
import { useAppSelector } from "@/store/hooks";
import { ISettings } from "@/slices/homeSlice";

export function AnnouncementBar() {
  const [dismissed, setDismissed] = useState(false);

  const SettingList = useAppSelector((state) => state.home.SettingList);
  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );

  // Read settings from database / Redux store
  const isEnabled = Settings?.["EnableAnnouncement"]?.SettingValue;
  if (isEnabled === "0" || isEnabled === "false") {
    return null;
  }

  // Use AnnouncementText from settings, with initial default fallback
  const announcementText = Settings?.["AnnouncementText"]?.SettingValue;

  if (dismissed || !announcementText) return null;

  // Dynamically format special keywords like "10% OFF" and promo codes
  const renderFormattedText = (text: string) => {
    if (!text) return null;

    // Matches patterns like "10% OFF", "15% OFF", "Use Code: WELCOME10", "WELCOME10"
    const regex = /(\d+%\s*OFF|Use Code:\s*[A-Z0-9]+|WELCOME[0-9]*)/gi;
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (/^\d+%\s*OFF$/i.test(part)) {
        return (
          <span
            key={index}
            className="text-white bg-[#1c2612] font-extrabold px-2.5 py-0.5 rounded-full shadow-sm border border-[#1c2612]/30 mx-1 inline-block tracking-wider"
          >
            {part}
          </span>
        );
      }

      if (/^(Use Code:\s*[A-Z0-9]*)$/i.test(part)) {
        return (
          <span
            key={index}
            className="text-white bg-[#1c2612] font-extrabold px-2.5 py-0.5 rounded-full shadow-sm border border-[#1c2612]/30 mx-1 inline-block tracking-wider"
          >
            {part}
          </span>
        );
      }

      return part;
    });
  };

  return (
    <div className="w-full bg-gradient-to-r from-[#d4af37] via-[#fce881] to-[#d4af37] text-[#1c2612] border-b border-[#b89530]/60 py-2.5 px-3 sm:px-6 relative z-[60] font-jost transition-all duration-300">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex-1 text-center text-xs sm:text-sm font-bold tracking-wide text-[#1c2612] leading-normal flex items-center justify-center flex-wrap gap-y-1">
          {renderFormattedText(announcementText)}
        </div>

        <button
          onClick={() => setDismissed(true)}
          className="text-[#1c2612]/80 hover:text-[#1c2612] p-1 rounded-full hover:bg-black/10 transition-colors cursor-pointer shrink-0"
          aria-label="Dismiss banner"
          title="Dismiss banner"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
